import React, { useMemo } from 'react';
import classnames from 'classnames';
import { useTranslation } from 'react-i18next';
import { PapiDeviation } from '@wix/da-papi-types';
import { IconSize } from '@wix/da-ds/pkg/Icons/IconWrap';
import AttachIcon from '@wix/da-ds/pkg/Icons/16x16/Attach';
import FavStarIcon from '@wix/da-ds/pkg/Icons/32x32/FavStar';
import DownloadIcon from '@wix/da-ds/pkg/Icons/24x24/Download';
import PremiumDownloadIcon from '@wix/da-ds/pkg/Icons/24x24/PremiumDownload';
import { useIsCurrentUserDeviationAuthor } from '../../../../utils/hooks/useIsCurrentUserDeviationAuthor';
import { getDeviationPremiumDownload } from '../../../../ActionBar/buttons/DownloadButton/permissions';
import BuyPremiumContentButton from './_partials/BuyPremiumContentButton';
import AttachmentsList from './_partials/AttachmentsList/AttachmentsList';
import DiscountsPopupTrigger from '../../../../DiscountsPopupTrigger/DiscountsPopupTrigger';
import { getDeviationSaleDetails } from '../../../../utils/deviations';

import s from './PcpInfo.scss';

const MIN_VISIBLE_STAT_COUNT = 2;

interface Props {
  deviation: PapiDeviation;
  className?: string;
}

function useHeadingText(deviation: PapiDeviation) {
  const { t } = useTranslation();
  const isOwner = useIsCurrentUserDeviationAuthor(deviation);
  const { permission } = getDeviationPremiumDownload(deviation, isOwner);

  if (permission === 'downloadable') {
    return deviation.isPublished
      ? t('sidebar.pcpInfo.heading.hasPurchased.published')
      : t('sidebar.pcpInfo.heading.hasPurchased.private');
  }

  return deviation.isPublished
    ? t('sidebar.pcpInfo.heading.default.published')
    : t('sidebar.pcpInfo.heading.default.private');
}

const PcpInfo: React.FC<Props> = ({ deviation, className }) => {
  const { t } = useTranslation();
  const headingText = useHeadingText(deviation);
  const { eligibleDiscount, allDiscounts, hasDiscounts } = useMemo(
    () => getDeviationSaleDetails(deviation),
    [deviation]
  );
  if (!deviation.pcp || !deviation.extended?.pcp) {
    return null; // should not get here
  }

  const { salesCount: sales = 0 } = deviation.pcp;
  const { favourites = 0 } = deviation.stats ?? {};
  const { assets = [] } = deviation.extended.pcp;
  const showFavesStat = favourites >= MIN_VISIBLE_STAT_COUNT;
  const showSalesStat = showFavesStat && sales >= MIN_VISIBLE_STAT_COUNT;

  return (
    <div className={classnames(s['root'], className)}>
      <div className={s['heading']}>
        {<PremiumDownloadIcon className={s['premium-download-icon']} size={IconSize.MEDIUM} />}
        {headingText}
      </div>

      <div className={s['pcp-stats']}>
        {showFavesStat && (
          <div className={s['pcp-stat']}>
            <FavStarIcon size={IconSize.SMALLER} disableDefaultColors />
            {t('sidebar.pcpInfo.stats.favourites', { count: favourites })}
          </div>
        )}
        {showSalesStat && (
          <div className={s['pcp-stat']}>
            <DownloadIcon size={IconSize.SMALLER} disableDefaultColors />
            {t('sidebar.pcpInfo.stats.sold', { count: sales })}
          </div>
        )}
      </div>

      <span className={s['button']}>
        <BuyPremiumContentButton deviation={deviation} />
      </span>

      {hasDiscounts && (
        <div className={s['discounts-popup']}>
          <DiscountsPopupTrigger
            eligibleDiscount={eligibleDiscount}
            allDiscounts={allDiscounts}
            className={s['discounts-popup-trigger']}
          />
        </div>
      )}

      {assets.length > 0 && (
        <div className={s['attachments-count']}>
          <AttachIcon size={IconSize.SMALLER} disableDefaultColors />
          {t('sidebar.pcpInfo.attachedFiles', { count: assets.length })}
        </div>
      )}

      <AttachmentsList assets={assets} />
    </div>
  );
};
PcpInfo.displayName = 'PcpInfo';

export default PcpInfo;
